// Where the texts drawn over the Bodyweight trend chart go (goal label, pace
// label, "PROJECTION" title), so that none lands on another
// or on the series. Pure functions over the axis scales and plot offset that
// recharts hands to <Customized>; BodyweightChartAnnotations.js draws the result.

export const firstScale = (axisMap) => Object.values(axisMap)[0].scale;

// Room (px) the two-line goal label needs above or below its dot.
const GOAL_LABEL_ROOM = 44;
const LABEL_GAP = 4; // horizontal breathing room between two texts
export const PROJECTION_LABEL_SIZE = 11;
const DATA_CLEARANCE = 5;
const SAMPLE_EVERY_PX = 3;
// Gap between a goal / pace label and the dot it belongs to.
const LABEL_DOT_GAP = 16;
// Baseline of the goal label and of its date line, from the goal dot: both
// above it (label first), or both below it.
const GOAL_TEXT_OFFSET = 30;
const GOAL_DATE_OFFSET_ABOVE = 14;
const GOAL_DATE_OFFSET_BELOW = 46;

// Average glyph width as a share of the font size, by kind of text. SVG gives
// no measurements during render, so texts are measured by their length.
const CHAR_WIDTH = { regular: 0.6, bold: 0.66, spaced: 0.78 };
const textWidth = (text, size, weight = "regular") =>
  text.length * size * CHAR_WIDTH[weight];

// Rough bounding box of a text (SVG gives no measurements during render). The
// width is an estimate, so the boxes are padded when compared.
const textBox = ({ x, y, text, size, anchor, weight = "regular" }) => {
  const width = textWidth(text, size, weight);
  const x0 = anchor === "end" ? x - width : x;
  return { x0, x1: x0 + width, y0: y - size, y1: y + 3 };
};

const boxesOverlap = (a, b) =>
  a.x0 < b.x1 + LABEL_GAP &&
  b.x0 < a.x1 + LABEL_GAP &&
  a.y0 < b.y1 &&
  b.y0 < a.y1;

const overlapsAny = (box, others) =>
  others.some((other) => boxesOverlap(box, other));

const pointInBox = (px, py, box) =>
  px >= box.x0 - DATA_CLEARANCE &&
  px <= box.x1 + DATA_CLEARANCE &&
  py >= box.y0 - DATA_CLEARANCE &&
  py <= box.y1 + DATA_CLEARANCE;

// The series in pixels: the weigh-in dots and the trend, path and pace lines
// (each a list of {x, y}), so texts can be kept off them.
export const toPixelShapes = (rows, xScale, yScale) => {
  const pick = (key) =>
    rows
      .filter((row) => row[key] != null)
      .map((row) => ({ x: xScale(row.idx), y: yScale(row[key]) }));
  return {
    dots: pick("weight"),
    lines: [pick("trendLine"), pick("path"), pick("pace")],
  };
};

const hitsData = (box, shapes) => {
  if (shapes.dots.some((dot) => pointInBox(dot.x, dot.y, box))) return true;
  return shapes.lines.some((line) =>
    line.some((from, i) => {
      const to = line[i + 1];
      if (!to) return pointInBox(from.x, from.y, box);
      const steps = Math.max(
        1,
        Math.ceil(Math.hypot(to.x - from.x, to.y - from.y) / SAMPLE_EVERY_PX)
      );
      for (let s = 0; s <= steps; s++) {
        const ratio = s / steps;
        const x = from.x + (to.x - from.x) * ratio;
        const y = from.y + (to.y - from.y) * ratio;
        if (pointInBox(x, y, box)) return true;
      }
      return false;
    })
  );
};

const isFree = (boxes, avoid, shapes) =>
  boxes.every((box) => !overlapsAny(box, avoid) && !hitsData(box, shapes));

const preferredPaceLabelY = ({
  paceY,
  goalY,
  goalTextY,
  goalDateY,
  labelsBelow,
  plotTop,
}) => {
  const paceAbove = paceY <= goalY;
  if (labelsBelow) {
    return paceAbove
      ? Math.max(paceY - 14, plotTop + 10)
      : Math.max(paceY + 22, goalDateY + 18);
  }
  return paceAbove
    ? Math.max(Math.min(paceY - 14, goalTextY - 22), 12)
    : paceY + 22;
};

// Where the pace label goes: next to the pace dot, on the side away from the
// goal label so the two never overlap. The first of these that sits on neither
// another label nor the series wins.
const paceLabelCandidates = (params) => {
  const { paceY, plotTop } = params;
  const preferred = preferredPaceLabelY(params);
  return [preferred, paceY - 14, paceY + 22, paceY - 32, paceY + 40].filter(
    (y) => y >= plotTop + 10
  );
};

const buildProjectionLayout = ({ scene, labelsBelow, labels, shapes }) => {
  const { x, goalY, paceY, plotTop } = scene;
  const labelX = x - LABEL_DOT_GAP;
  const goalTextY = labelsBelow
    ? goalY + GOAL_TEXT_OFFSET
    : goalY - GOAL_TEXT_OFFSET;
  const goalDateY = labelsBelow
    ? goalY + GOAL_DATE_OFFSET_BELOW
    : goalY - GOAL_DATE_OFFSET_ABOVE;
  const boxes = [
    textBox({
      x: labelX,
      y: goalTextY,
      text: labels.goal,
      size: 12,
      anchor: "end",
      weight: "bold",
    }),
    textBox({
      x: labelX,
      y: goalDateY,
      text: labels.by,
      size: 11,
      anchor: "end",
    }),
  ];
  let paceTextY = 0;
  if (paceY !== null) {
    const candidates = paceLabelCandidates({
      paceY,
      goalY,
      goalTextY,
      goalDateY,
      labelsBelow,
      plotTop,
    });
    const paceBoxAt = (y) =>
      textBox({
        x: labelX,
        y,
        text: labels.pace,
        size: 12,
        anchor: "end",
      });
    paceTextY =
      candidates.find((y) => isFree([paceBoxAt(y)], boxes, shapes)) ??
      candidates[0];
    boxes.push(paceBoxAt(paceTextY));
  }
  const clean = isFree(boxes.slice(0, 2), [], shapes);
  return {
    x,
    goalY,
    paceY,
    labelX,
    goalTextY,
    goalDateY,
    paceTextY,
    boxes,
    clean,
  };
};

// Positions of the goal marker and its labels. The goal label sits above its
// dot, unless there is no room for it there (a goal near the top edge would
// push it out of the plot, across the area's border) or the lines arrive from
// above (a loss goal: they would run through the text) — then it goes below.
// If the chosen side lands on the series and the other side has room, the
// other side is used.
export const layoutProjection = ({
  xAxisMap,
  yAxisMap,
  offset,
  goalIdx,
  startValue,
  targetValue,
  projectedValue,
  labels,
  shapes,
}) => {
  const yScale = firstScale(yAxisMap);
  const goalY = yScale(targetValue);
  const scene = {
    x: firstScale(xAxisMap)(goalIdx),
    goalY,
    paceY: projectedValue === null ? null : yScale(projectedValue),
    plotTop: offset.top,
  };
  const fitsAbove = goalY - offset.top >= GOAL_LABEL_ROOM;
  const fitsBelow = offset.top + offset.height - goalY >= GOAL_LABEL_ROOM + 20;
  const linesArriveFromAbove = yScale(startValue) < goalY - 4;
  const labelsBelow = fitsBelow && (!fitsAbove || linesArriveFromAbove);
  const first = buildProjectionLayout({ scene, labelsBelow, labels, shapes });
  const otherFits = labelsBelow ? fitsAbove : fitsBelow;
  if (first.clean || !otherFits) return first;
  const second = buildProjectionLayout({
    scene,
    labelsBelow: !labelsBelow,
    labels,
    shapes,
  });
  return second.clean ? second : first;
};

// "PROJECTION" at the top-left of the shaded area, or at its bottom-left when
// the top is taken. It is left out when the area is too narrow to hold it or
// when something would sit on top of it either way.
export const layoutProjectionTitle = ({
  todayX,
  goalX,
  plotTop,
  plotBottom,
  text,
  avoid,
  shapes,
}) => {
  const x = todayX + 16;
  for (const y of [plotTop + 18, plotBottom - 10]) {
    const box = textBox({
      x,
      y,
      text,
      size: PROJECTION_LABEL_SIZE,
      anchor: "start",
      weight: "spaced",
    });
    if (box.x1 + 8 <= goalX && isFree([box], avoid, shapes)) {
      return { x, y, box };
    }
  }
  return null;
};
